import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Group, Menu} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ToolbarAction} from './ToolbarAction';

const DEFAULT_OVERFLOW_LABEL = 'More options';

export interface ActionToolbarProps {
  /** Already-resolved actions — this component performs no filtering or injection. */
  actions: Array<ToolbarAction>;
  /** Accessible label for the overflow ⋮ trigger. */
  overflowLabel?: string;
}

/**
 * A presentational action toolbar shared across Benchling. It has no knowledge of the product
 * surface it lives on, of archival, or of feature flags: it takes a flat list of {@link ToolbarAction}s,
 * partitions them by `placement`, and renders a leading group set apart from the main group of quick
 * actions, plus an overflow ⋮ menu. Consumers resolve which actions apply (see
 * `archive/toolbar/archiveToolbarActions` for the archive/unarchive flow) before rendering.
 */
function ActionToolbar({actions, overflowLabel = DEFAULT_OVERFLOW_LABEL}: ActionToolbarProps): JSX.Element {
  const leadingActions = actions.filter((action) => action.placement === 'leading');
  const mainActions = actions.filter((action) => action.placement === 'main');
  const overflowActions = actions.filter((action) => action.placement === 'overflow');

  return (
    <Group spacing="md" {...getTestProps({component: 'ActionToolbar', element: 'root'})}>
      {leadingActions.length > 0 && (
        <Group spacing="xs">
          {leadingActions.map((action) => (
            <React.Fragment key={action.key}>{action.render()}</React.Fragment>
          ))}
        </Group>
      )}
      {(mainActions.length > 0 || overflowActions.length > 0) && (
        <Group spacing="xs">
          {mainActions.map((action) => (
            <React.Fragment key={action.key}>{action.render()}</React.Fragment>
          ))}
          {overflowActions.length > 0 && (
            <Menu position="bottom-end" withinPortal={true}>
              <Menu.Target>
                <ActionIcon
                  variant="tertiary"
                  aria-label={overflowLabel}
                  {...getTestProps({component: 'ActionToolbar', element: 'overflowButton'})}
                >
                  <BenchlingIcon icon="ellipsis-vertical" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {overflowActions.map((action) => (
                  <React.Fragment key={action.key}>{action.render()}</React.Fragment>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      )}
    </Group>
  );
}

/**
 * Memoized so a toolbar whose `actions` keep their identity (e.g. built in a `useMemo`) skips re-rendering when
 * its parent re-renders.
 */
export default React.memo(ActionToolbar);
